import { environment } from "../../config/environment";
import React, { useState, useEffect, useCallback, useMemo, useRef, useDeferredValue } from "react";
import Sidebar from "../Sidebar";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { Badge } from "../ui/Badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/Select";
import {
  Search,
  FileText,
  Plus,
  X,
  Trash2,
  Loader2,
  GitCompare,
  Sparkles,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/Dialog";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "../../hooks/use-toast";
import { Toaster } from "../ui/Toaster";
import { customFetch } from "../../utils/Auth";
import TemplateSummaryPanel from "./TemplateSummaryPanel";
import TemplateCompareModal from "./TemplateCompareModal";
import TemplateCompareSetup from "./TemplateCompareSetup";
import TrackChangesEditor from "./TrackChangesEditor";
import { getTemplateDisplayTime } from "./timeUtils";
import { templatePath } from "./templateUrls";
import { resolveDeepLinkTemplate } from "./deepLink";

export { templatePath };

// Order two templates as [older (left), newer (right)] for the diff view. The
// default version is treated as the "newer"/standard side regardless of its
// timestamp (timestamps are unreliable). If neither or both are default, fall
// back to display time.
const orderComparePair = (x, y) => {
  if (x.is_default === true && y.is_default !== true) return [y, x];
  if (y.is_default === true && x.is_default !== true) return [x, y];
  return getTemplateDisplayTime(x) <= getTemplateDisplayTime(y)
    ? [x, y]
    : [y, x];
};

// Shared styling for the per-group header action buttons (Compare, Add
// Version) so they always render identically.
const GROUP_ACTION_BUTTON_CLASS =
  "flex items-center gap-2 px-3 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400";

const getTemplateTypeBadgeClass = (type) => {
  if (type === "SOAP_NOTE_TEMPLATE") {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }
  if (type === "CONVERSATION_PROMPT") {
    return "border-green-200 bg-green-50 text-green-700";
  }
  if (type === "SECONDARY_PROMPT") {
    return "border-purple-200 bg-purple-50 text-purple-700";
  }
  return "border-gray-200 bg-gray-50 text-gray-700";
};

// Stable empty-array reference so non-active group cards keep identical props
// across selection toggles and React.memo can skip re-rendering them.
const EMPTY_SELECTION = [];

const TemplateGroupCard = React.memo(function TemplateGroupCard({
  templateName,
  templateGroup,
  formatTimestamp,
  onAddVersion,
  onDeleteGroup,
  onOpenTemplateDetails,
  onSetDefault,
  onUpdateScope,
  onDeleteVersion,
  isCompareMode,
  compareSelection,
  onToggleCompareMode,
  onToggleCompareSelect,
  onViewDiff,
}) {
  const defaultTemplate = templateGroup.find((t) => t.is_default === true);
  const displayType = defaultTemplate?.type || templateGroup[0]?.type || "N/A";
  const selectedCount = isCompareMode ? compareSelection.length : 0;
  const canCompare = templateGroup.length >= 2;

  // When not comparing, collapse the group down to just the default (or first)
  // version and reveal the rest behind a toggle.
  const [showAllVersions, setShowAllVersions] = useState(false);
  const primaryTemplate = defaultTemplate || templateGroup[0];
  const otherVersionsCount = templateGroup.length - 1;
  const hasOtherVersions = otherVersionsCount > 0;
  // When expanded, keep the default/primary version pinned to the top.
  const orderedTemplates = primaryTemplate
    ? [primaryTemplate, ...templateGroup.filter((t) => t !== primaryTemplate)]
    : templateGroup;

  // Renders a single version row; shared between the always-visible primary
  // row and the collapsible "other versions" rows below it.
  const renderTemplateRow = (template) => {
    const isSelected =
      isCompareMode && compareSelection.includes(template.version);
    return (
      <div
        key={`${template.name}-${template.version}`}
        className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer ${
          isSelected
            ? "border-ktx-purple bg-purple-50"
            : template.is_default === true
            ? "border-purple-200 bg-purple-50/40 hover:bg-purple-50"
            : "border-gray-200 hover:bg-gray-50"
        }`}
        onClick={() =>
          isCompareMode
            ? onToggleCompareSelect(template.version)
            : onOpenTemplateDetails(template)
        }
      >
        <div className="flex items-center gap-3 min-w-0">
          {isCompareMode && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleCompareSelect(template.version)}
              onClick={(e) => e.stopPropagation()}
              className="relative h-5 w-5 shrink-0 cursor-pointer appearance-none rounded-[4px] border-2 border-ktx-purple bg-white transition-colors checked:bg-ktx-purple after:absolute after:left-[5px] after:top-[1px] after:h-[10px] after:w-[6px] after:rotate-45 after:border-b-2 after:border-r-2 after:border-white after:opacity-0 after:content-[''] checked:after:opacity-100 disabled:cursor-not-allowed disabled:opacity-40"
            />
          )}
          <div className="flex flex-col min-w-[60px]">
            <div className="flex items-center gap-2">
              {template.is_default === true && (
                <Badge
                  variant="outline"
                  className="text-sm font-bold px-2.5 py-0.5 border-purple-200 bg-purple-100 text-purple-800"
                >
                  Default
                </Badge>
              )}
              <span className="font-medium text-gray-900">
                {template.version || "v1.0"}
              </span>
            </div>
            {(() => {
              const displayTime = getTemplateDisplayTime(template);
              if (!template.username && !displayTime) return null;
              return (
                <div className="text-xs text-gray-400 mt-1">
                  {template.username && (
                    <span className="font-medium text-gray-600">
                      {template.username}
                    </span>
                  )}
                  {template.username && displayTime && <span> • </span>}
                  {displayTime && formatTimestamp(displayTime)}
                </div>
              );
            })()}
          </div>
        </div>
        {!isCompareMode && (
          <div className="flex items-center gap-2">
            <div className="w-28 flex justify-end">
              {template.is_default !== true && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetDefault(template.name, template.version);
                  }}
                  variant="outline"
                  size="sm"
                  className="text-xs px-3 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                >
                  Make Default
                </Button>
              )}
            </div>
            <Select
              value={template.scope || ""}
              onValueChange={(newScope) =>
                onUpdateScope(template.name, template.version, newScope)
              }
            >
              <SelectTrigger className="w-32 h-8 text-xs hover:bg-gray-50/60 hover:border-gray-400 transition-colors">
                <SelectValue placeholder="Select scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PROVIDER">PROVIDER</SelectItem>
                <SelectItem value="SPECIALTY">SPECIALTY</SelectItem>
              </SelectContent>
            </Select>
            <div className="w-8 flex justify-center">
              {template.is_default === true ? (
                <div className="w-6 h-6" />
              ) : (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteVersion(template.name, template.version);
                  }}
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                  title={`Delete version ${template.version} of ${template.name}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
          <Card key={templateName}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge
                    variant="outline"
                    className={`text-xs ${getTemplateTypeBadgeClass(displayType)}`}
                  >
                    {displayType}
                  </Badge>
                  <div>
                    <CardTitle className="text-lg">{templateName}</CardTitle>
                  </div>
                  <div className="text-sm text-gray-500">
                    {templateGroup.length} version
                    {templateGroup.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {isCompareMode ? (
                    <>
                      <span className="text-sm text-gray-500 mr-1">
                        Select 2 versions to compare
                      </span>
                      <Button
                        onClick={() => onViewDiff(templateGroup)}
                        disabled={selectedCount !== 2}
                        variant="outline"
                        size="sm"
                        className={`${GROUP_ACTION_BUTTON_CLASS} ${
                          selectedCount === 2
                            ? "border-ktx-purple bg-purple-50 text-ktx-purple hover:bg-purple-100 hover:border-ktx-purple"
                            : "opacity-50 cursor-not-allowed"
                        }`}
                      >
                        <GitCompare className="h-4 w-4" />
                        View diff ({selectedCount}/2)
                      </Button>
                      <Button
                        onClick={() =>
                          onToggleCompareMode(templateName, templateGroup)
                        }
                        variant="ghost"
                        size="sm"
                        className="px-3 py-1.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      {canCompare && (
                        <Button
                          onClick={() =>
                          onToggleCompareMode(templateName, templateGroup)
                        }
                          variant="outline"
                          size="sm"
                          className={GROUP_ACTION_BUTTON_CLASS}
                        >
                          <GitCompare className="h-4 w-4" />
                          Compare
                        </Button>
                      )}
                      <Button
                        onClick={() => onAddVersion(templateName, templateGroup)}
                        variant="outline"
                        size="sm"
                        className={GROUP_ACTION_BUTTON_CLASS}
                      >
                        <Plus className="h-4 w-4" />
                        Add Version
                      </Button>
                      <Button
                        onClick={() => onDeleteGroup(templateName)}
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete all versions of this template"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {primaryTemplate ? (
                  <>
                    {renderTemplateRow(primaryTemplate)}
                    {!isCompareMode && hasOtherVersions && (
                      <button
                        type="button"
                        aria-expanded={showAllVersions}
                        onClick={() => setShowAllVersions((prev) => !prev)}
                        className="flex items-center gap-1.5 pl-1 py-0.5 text-[15px] font-bold text-ktx-purple hover:text-purple-700"
                      >
                        {showAllVersions ? (
                          <>
                            <ChevronUp className="h-5 w-5" />
                            Hide other versions
                          </>
                        ) : (
                          <>
                            <ChevronDown className="h-5 w-5" />
                            Show {otherVersionsCount} other version
                            {otherVersionsCount !== 1 ? "s" : ""}
                          </>
                        )}
                      </button>
                    )}
                    {(isCompareMode || showAllVersions) &&
                      orderedTemplates
                        .filter((template) => template !== primaryTemplate)
                        .map((template) => renderTemplateRow(template))}
                  </>
                ) : (
                  templateGroup.map((template) => renderTemplateRow(template))
                )}
              </div>
            </CardContent>
          </Card>
  );
});

const TemplateGroupsList = React.memo(function TemplateGroupsList({
  groupedTemplateEntries,
  formatTimestamp,
  onAddVersion,
  onDeleteGroup,
  onOpenTemplateDetails,
  onSetDefault,
  onUpdateScope,
  onDeleteVersion,
  compareGroup,
  compareSelection,
  onToggleCompareMode,
  onToggleCompareSelect,
  onViewDiff,
}) {
  return (
    <div className="space-y-6">
      {groupedTemplateEntries.map(([templateName, templateGroup]) => {
        const isCompareMode = compareGroup === templateName;
        return (
          <TemplateGroupCard
            key={templateName}
            templateName={templateName}
            templateGroup={templateGroup}
            formatTimestamp={formatTimestamp}
            onAddVersion={onAddVersion}
            onDeleteGroup={onDeleteGroup}
            onOpenTemplateDetails={onOpenTemplateDetails}
            onSetDefault={onSetDefault}
            onUpdateScope={onUpdateScope}
            onDeleteVersion={onDeleteVersion}
            isCompareMode={isCompareMode}
            compareSelection={isCompareMode ? compareSelection : EMPTY_SELECTION}
            onToggleCompareMode={onToggleCompareMode}
            onToggleCompareSelect={onToggleCompareSelect}
            onViewDiff={onViewDiff}
          />
        );
      })}
    </div>
  );
});

const Templates = ({ setAuth, setUserOrg }) => {
  const { toast } = useToast();
  const { templateName } = useParams();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(templateName || "");
  const [pendingDeepLink, setPendingDeepLink] = useState(templateName || "");
  const searchQueryRef = useRef(searchQuery);
  searchQueryRef.current = searchQuery;
  const [typeFilter, setTypeFilter] = useState("ALL");
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const filteredTemplates = useMemo(() => {
    let filtered = templates;
    if (typeFilter !== "ALL") {
      filtered = filtered.filter((template) => template.type === typeFilter);
    }
    if (deferredSearchQuery.trim() !== "") {
      filtered = filtered.filter((template) =>
        template.name.toLowerCase().includes(deferredSearchQuery.toLowerCase())
      );
    }
    return filtered;
  }, [templates, typeFilter, deferredSearchQuery]);
  const [templateDetails, setTemplateDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ name: "", version: null });
  const [newTemplateDialogOpen, setNewTemplateDialogOpen] = useState(false);
  const [isNewTemplate, setIsNewTemplate] = useState(true); // true for new template, false for new version
  const [newTemplateFormData, setNewTemplateFormData] = useState({
    name: "",
    version: "",
    type: "SOAP_NOTE_TEMPLATE",
    scope: "SPECIALTY",
    text: "",
  });
  // Reference text the inline track-changes editor diffs against (the previous
  // default version's content; empty for a brand-new template).
  const [newTemplateBaseText, setNewTemplateBaseText] = useState("");
  const [templateNameError, setTemplateNameError] = useState("");
  const [templateVersionError, setTemplateVersionError] = useState("");
  const [templateDetailsDialogOpen, setTemplateDetailsDialogOpen] =
    useState(false);
  const [selectedTemplateForDetails, setSelectedTemplateForDetails] =
    useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Compare flow: which group is in selection mode, the selected versions
  // (max 2), and the resolved older/newer pair shown in the diff modal.
  const [compareGroup, setCompareGroup] = useState(null);
  const [compareSelection, setCompareSelection] = useState([]);
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [comparePair, setComparePair] = useState({ a: null, b: null });
  // Mirror selection in a ref so handlers that read it (e.g. View diff) can stay
  // referentially stable and not break React.memo on the group cards each toggle.
  const compareSelectionRef = useRef(compareSelection);
  compareSelectionRef.current = compareSelection;
  const compareGroupRef = useRef(compareGroup);
  compareGroupRef.current = compareGroup;

  // Page-level compare: a search-driven setup modal to pick two templates.
  const [compareSetupOpen, setCompareSetupOpen] = useState(false);

  const templateNamesSet = useMemo(
    () => new Set(templates.map((template) => template.name)),
    [templates]
  );

  const templateVersionsByName = useMemo(() => {
    const versionsByName = new Map();
    templates.forEach((template) => {
      if (!versionsByName.has(template.name)) {
        versionsByName.set(template.name, new Set());
      }
      versionsByName.get(template.name).add(template.version);
    });
    return versionsByName;
  }, [templates]);

  // Check if template name already exists (memoized)
  const checkTemplateNameExists = useCallback(
    (templateName) => templateNamesSet.has(templateName),
    [templateNamesSet]
  );

  // Check if version already exists for a template (memoized)
  const checkTemplateVersionExists = useCallback(
    (templateName, version) => {
      const versions = templateVersionsByName.get(templateName);
      return versions ? versions.has(version) : false;
    },
    [templateVersionsByName]
  );

  const getCurrentUsername = useCallback(() => {
    return localStorage.getItem("username") || "unknown";
  }, []);

  const formatTimestamp = useCallback((timestamp) => {
    if (!timestamp) return 'Unknown';
    try {
      // Handle Unix timestamp in seconds (convert to milliseconds)
      const date = new Date(timestamp * 1000);
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return 'Invalid date';
    }
  }, []);

  // Editing the search box cancels a deep link that hasn't opened yet, so a
  // term the reader moved on from can never pop a dialog later.
  const updateSearchQuery = useCallback((value) => {
    setSearchQuery(value);
    setPendingDeepLink("");
  }, []);

  // Keep the search box in sync when the route param changes from outside this
  // component (e.g. navigating between deep links or using back/forward).
  useEffect(() => {
    const paramQuery = templateName || "";
    if (searchQueryRef.current.trim() === paramQuery) return;
    setSearchQuery(paramQuery);
    setPendingDeepLink(paramQuery);
  }, [templateName]);

  useEffect(() => {
    const target = templatePath(searchQuery);
    if (window.location.pathname !== target) {
      navigate(target, { replace: true });
    }
  }, [searchQuery, navigate]);

  // Check for template name conflicts when form data changes
  useEffect(() => {
    if (newTemplateFormData.name.trim() && isNewTemplate) {
      if (checkTemplateNameExists(newTemplateFormData.name)) {
        setTemplateNameError("Template name already exists");
      } else {
        setTemplateNameError("");
      }
    } else {
      setTemplateNameError("");
    }
  }, [newTemplateFormData.name, isNewTemplate, checkTemplateNameExists]);

  // Check for template version conflicts when form data changes
  useEffect(() => {
    if (newTemplateFormData.name.trim() && newTemplateFormData.version.trim()) {
      if (checkTemplateVersionExists(newTemplateFormData.name, newTemplateFormData.version)) {
        setTemplateVersionError("Version already exists for this template");
      } else {
        setTemplateVersionError("");
      }
    } else {
      setTemplateVersionError("");
    }
  }, [newTemplateFormData.name, newTemplateFormData.version, checkTemplateVersionExists]);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      if (!token) {
        setError("No authentication token found");
        setLoading(false);
        return;
      }

      const response = await customFetch(`${environment.apiUrl}/templates/get-all-templates`, {
        headers: {
          token: token,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTemplates(data);
      setError(null);
    } catch (err) {
      console.error("Error fetching templates:", err);
      setError("Failed to fetch templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const fetchTemplateDetails = useCallback(async (templateName, version) => {
    try {
      console.log("Fetching template details for:", templateName, version);
      setLoadingDetails(true);
      setTemplateDetails(null);
      setError(null);

      const token = localStorage.getItem("token");

      if (!token) {
        setError("No authentication token found");
        setLoadingDetails(false);
        return;
      }

      const response = await customFetch(
        `${environment.apiUrl}/templates/get-template?name=${encodeURIComponent(
          templateName
        )}&version=${encodeURIComponent(version)}`,
        {
          method: "GET",
          headers: {
            token: token,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTemplateDetails(data);
      setLoadingDetails(false);
    } catch (err) {
      console.error("Error fetching template details:", err);
      setError("Failed to fetch template details");
      setLoadingDetails(false);
    }
  }, []);

  const setTemplateAsDefault = useCallback(async (templateName, version) => {
    try {
      const token = localStorage.getItem("token");

      const response = await customFetch(`${environment.apiUrl}/templates/set-default`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          token: token,
        },
        body: JSON.stringify({
          name: templateName,
          version: version,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.details || "Failed to set template as default"
        );
      }

              const data = await response.json();
        console.log("Template set as default successfully:", data);

        // Show success toast
        toast({
          title: "Success",
          description: `Version "${version}" set as default successfully`,
        });

        // Refresh templates to show updated default status
        fetchTemplates();
          } catch (error) {
        console.error("Error setting template as default:", error);
        const errorMessage = error.response?.data?.message || error.message || "Unknown error occurred";
        toast({
          variant: "destructive",
          title: "Error",
          description: `Error setting template as default: ${errorMessage}`,
        });
      }
  }, [fetchTemplates, toast]);

  const updateTemplateScope = useCallback(async (templateName, version, newScope) => {
    try {
      const token = localStorage.getItem("token");

      const headers = {
        "Content-Type": "application/json",
        token: token,
      };
      console.log("Request headers:", headers);

      const response = await customFetch(`${environment.apiUrl}/templates/set-scope`, {
        method: "PATCH",
        headers: headers,
        body: JSON.stringify({
          name: templateName,
          version: version,
          new_scope: newScope,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || "Failed to update template scope");
      }

              const data = await response.json();
        console.log("Template scope updated successfully:", data);

        // Show success toast
        toast({
          title: "Success",
          description: "Template scope updated successfully",
        });

        // Refresh templates to show updated scope
        fetchTemplates();
          } catch (error) {
        console.error("Error updating template scope:", error);
        const errorMessage = error.response?.data?.message || error.message || "Unknown error occurred";
        toast({
          variant: "destructive",
          title: "Error",
          description: `Error updating template scope: ${errorMessage}`,
        });
      }
  }, [fetchTemplates, toast]);

  const deleteTemplate = useCallback(async (templateName, version = null) => {
    try {
      const token = localStorage.getItem("token");

      const response = await customFetch(`${environment.apiUrl}/templates/delete-template`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          token: token,
        },
        body: JSON.stringify({
          name: templateName,
          ...(version && { version }),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || "Failed to delete template");
      }

              const data = await response.json();
        console.log("Template deleted successfully:", data);

        // Show success toast
        toast({
          title: "Success",
          description: "Template deleted successfully",
        });

        // Refresh templates to show updated list
        fetchTemplates();
          } catch (error) {
        console.error("Error deleting template:", error);
        const errorMessage = error.response?.data?.message || error.message || "Unknown error occurred";
        toast({
          variant: "destructive",
          title: "Error",
          description: `Error deleting template: ${errorMessage}`,
        });
      }
  }, [fetchTemplates, toast]);

  const openDeleteDialog = useCallback((templateName, version = null) => {
    setDeleteTarget({ name: templateName, version });
    setDeleteDialogOpen(true);
  }, []);

  const openNewTemplateDialog = useCallback(async (isNew = true, existingTemplate = null) => {
    setIsNewTemplate(isNew);
    setTemplateNameError(""); // Clear any previous errors
    setTemplateVersionError(""); // Clear any previous errors
    if (isNew) {
      // New template - reset form
      setNewTemplateFormData({
        name: "",
        version: "",
        type: "SOAP_NOTE_TEMPLATE",
        scope: "SPECIALTY",
        text: "",
      });
      // No prior content to diff against, so everything typed reads as added.
      setNewTemplateBaseText("");
    } else {
      // New version - pre-fill with existing template data
      setNewTemplateFormData({
        name: existingTemplate.name,
        version: "",
        type: existingTemplate.type || "SOAP_NOTE_TEMPLATE",
        scope: existingTemplate.scope || "SPECIALTY",
        text: "",
      });
      setNewTemplateBaseText("");

      // Find the default version of this template and fetch its content
      try {
        const defaultTemplate = templates.find(
          (template) => template.name === existingTemplate.name && template.is_default === true
        );
        
        if (defaultTemplate) {
          const response = await customFetch(
            `${environment.apiUrl}/templates/get-template?name=${encodeURIComponent(
              defaultTemplate.name
            )}&version=${encodeURIComponent(defaultTemplate.version)}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          if (response.ok) {
            const templateData = await response.json();
            const baseText = templateData.text || "";
            setNewTemplateFormData(prev => ({
              ...prev,
              text: baseText,
            }));
            setNewTemplateBaseText(baseText);
          }
        }
      } catch (error) {
        console.error("Error fetching default template content:", error);
        // Continue with empty text if fetch fails
      }
    }
    setNewTemplateDialogOpen(true);
  }, [templates]);

  const openTemplateDetailsDialog = useCallback(async (template) => {
    setSelectedTemplateForDetails(template);
    setShowSummary(false); // always open on the template content view

    // Fetch template details first, then open dialog
    if (
      !templateDetails ||
      templateDetails.name !== template.name ||
      templateDetails.version !== template.version
    ) {
      await fetchTemplateDetails(template.name, template.version);
    }
    
    // Open dialog after data is loaded
    setTemplateDetailsDialogOpen(true);
  }, [fetchTemplateDetails, templateDetails]);

  const handleDeleteConfirm = useCallback(async () => {
    await deleteTemplate(deleteTarget.name, deleteTarget.version);
    setDeleteDialogOpen(false);
    setDeleteTarget({ name: "", version: null });
  }, [deleteTarget.name, deleteTarget.version, deleteTemplate]);

  const resetNewTemplateForm = useCallback(() => {
    setNewTemplateFormData({
      name: "",
      version: "",
      type: "SOAP_NOTE_TEMPLATE",
      scope: "SPECIALTY",
      text: "",
    });
    setNewTemplateBaseText("");
    setTemplateNameError("");
    setTemplateVersionError("");
    setIsSubmitting(false);
  }, []);

  // Optimized form input handlers
  const handleNameChange = useCallback((e) => {
    setNewTemplateFormData(prev => ({
      ...prev,
      name: e.target.value,
    }));
  }, []);

  const handleVersionChange = useCallback((e) => {
    setNewTemplateFormData(prev => ({
      ...prev,
      version: e.target.value,
    }));
  }, []);

  const handleTypeChange = useCallback((value) => {
    setNewTemplateFormData(prev => ({
      ...prev,
      type: value,
    }));
  }, []);

  const handleScopeChange = useCallback((value) => {
    setNewTemplateFormData(prev => ({
      ...prev,
      scope: value,
    }));
  }, []);

  const handleNewTemplateSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }

    // The inline editor isn't a native form field, so guard empty content here.
    if (!newTemplateFormData.text.trim()) {
      toast({
        variant: "destructive",
        title: "Template content required",
        description: "Please enter content for the template before saving.",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem("token");

      const response = await customFetch(`${environment.apiUrl}/templates/create-template`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          token: token,
        },
        body: JSON.stringify({
          ...newTemplateFormData,
          is_default: true, // Always set new templates/versions as default
          username: getCurrentUsername(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || "Failed to create template");
      }

      const data = await response.json();
      console.log("Template created successfully:", data);

      // Show success toast
      toast({
        title: "Success",
        description: "Template created successfully",
      });

      // Close dialog and refresh templates
      setNewTemplateDialogOpen(false);
      resetNewTemplateForm();
      fetchTemplates();
    } catch (error) {
      console.error("Error creating template:", error);
      const errorMessage = error.response?.data?.message || error.message || "Unknown error occurred";
      toast({
        variant: "destructive",
        title: "Error",
        description: `Error creating template: ${errorMessage}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    newTemplateFormData,
    fetchTemplates,
    getCurrentUsername,
    resetNewTemplateForm,
    toast,
  ]);

  // Helper function to count unique templates
  const countUniqueTemplates = useCallback((templateList) => {
    const uniqueNames = new Set(templateList.map((template) => template.name));
    return uniqueNames.size;
  }, []);


  // Group templates by name
  const groupTemplatesByName = useCallback((templatesToGroup) => {
    const grouped = {};
    templatesToGroup.forEach((template) => {
      const baseName = template.name;
      if (!grouped[baseName]) {
        grouped[baseName] = [];
      }

      // Use the actual template data - no mock versions
      grouped[baseName].push(template);
    });

    // Sort each group by creation/update time (latest first). Stable sort keeps
    // a deterministic order when two versions share a timestamp.
    Object.keys(grouped).forEach((key) => {
      grouped[key].sort(
        (a, b) => getTemplateDisplayTime(b) - getTemplateDisplayTime(a)
      );
    });

    // Sort template groups by the most recent time of any version in the group
    const sortedGroups = Object.entries(grouped).sort(([, groupA], [, groupB]) => {
      const maxTimeA = Math.max(...groupA.map((t) => getTemplateDisplayTime(t)));
      const maxTimeB = Math.max(...groupB.map((t) => getTemplateDisplayTime(t)));
      return maxTimeB - maxTimeA; // Most recent first
    });

    // Convert back to object with sorted keys
    const sortedGrouped = {};
    sortedGroups.forEach(([key, group]) => {
      sortedGrouped[key] = group;
    });

    return sortedGrouped;
  }, []);

  const groupedTemplates = useMemo(
    () => groupTemplatesByName(filteredTemplates),
    [filteredTemplates, groupTemplatesByName]
  );
  const groupedTemplateEntries = useMemo(
    () => Object.entries(groupedTemplates),
    [groupedTemplates]
  );
  const uniqueFilteredTemplateCount = useMemo(
    () => countUniqueTemplates(filteredTemplates),
    [countUniqueTemplates, filteredTemplates]
  );

  // Open the template a deep link names, once it has loaded. Only terms that
  // arrived in the URL qualify: typing rewrites the URL too, and opening a
  // dialog on the keystroke that happens to narrow to one match would fight the
  // reader. Fires at most once per link, so closing the dialog stays closed.
  useEffect(() => {
    if (!pendingDeepLink || loading || error) return;
    if (deferredSearchQuery.trim() !== pendingDeepLink) return;

    setPendingDeepLink("");
    const target = resolveDeepLinkTemplate(
      groupedTemplateEntries,
      pendingDeepLink
    );
    if (target) openTemplateDetailsDialog(target);
  }, [
    pendingDeepLink,
    loading,
    error,
    deferredSearchQuery,
    groupedTemplateEntries,
    openTemplateDetailsDialog,
  ]);
  const handleAddVersion = useCallback(
    async (templateName, templateGroup) => {
      await openNewTemplateDialog(false, {
        name: templateName,
        type: templateGroup[0]?.type || "SOAP_NOTE_TEMPLATE",
        scope: templateGroup[0]?.scope || "SPECIALTY",
      });
    },
    [openNewTemplateDialog]
  );

  // Toggle a group into/out of compare-selection mode. Only one group can be
  // in selection mode at a time. Entering pre-selects the group's default
  // version so the user just has to pick the one thing to compare it against.
  const handleToggleCompareMode = useCallback((templateName, templateGroup) => {
    const entering = compareGroupRef.current !== templateName;
    if (entering) {
      const defaultVersion = (templateGroup || []).find(
        (t) => t.is_default === true
      );
      setCompareSelection(
        defaultVersion && defaultVersion.version != null
          ? [defaultVersion.version]
          : []
      );
      setCompareGroup(templateName);
    } else {
      setCompareSelection([]);
      setCompareGroup(null);
    }
  }, []);

  // Add/remove a version from the selection. Capped at 2, but picking a third
  // drops the oldest (FIFO) so the two most-recent picks always win.
  const handleToggleCompareSelect = useCallback((version) => {
    setCompareSelection((prev) => {
      if (prev.includes(version)) {
        return prev.filter((v) => v !== version);
      }
      if (prev.length >= 2) {
        return [...prev.slice(1), version];
      }
      return [...prev, version];
    });
  }, []);

  // Resolve the two selected versions, order them older-left / newer-right,
  // open the diff modal, and exit selection mode.
  const handleViewDiff = useCallback((templateGroup) => {
    const selection = compareSelectionRef.current;
    if (selection.length !== 2) return;
    const picked = templateGroup.filter((t) => selection.includes(t.version));
    if (picked.length !== 2) return;
    const [older, newer] = orderComparePair(picked[0], picked[1]);
    setComparePair({ a: older, b: newer });
    setCompareModalOpen(true);
    setCompareGroup(null);
    setCompareSelection([]);
  }, []);

  // Page-level compare: the setup modal hands back the two chosen templates.
  const handleSetupCompare = useCallback((templateA, templateB) => {
    const [older, newer] = orderComparePair(templateA, templateB);
    setComparePair({ a: older, b: newer });
    setCompareModalOpen(true);
    setCompareSetupOpen(false);
  }, []);

  return (
    <div className="w-screen h-screen">
      <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
      <div
        className="px-8 flex items-start flex-col pt-8"
        style={{
          marginLeft: "var(--sidebar-width, 250px)",
          width: "calc(100% - var(--sidebar-width, 250px))",
        }}
      >
        <div className="w-full max-w-6xl space-y-6">
          {/* Search Section */}
          {
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Search className="h-4 w-4" />
                  Search Templates
                </CardTitle>
              </CardHeader>
                             <CardContent>
                 <div className="flex gap-3">
                   <div className="flex-1 relative">
                     <Input
                       placeholder="Search templates by name..."
                       value={searchQuery}
                       onChange={(e) => updateSearchQuery(e.target.value)}
                       className="pr-10"
                     />
                     {searchQuery && (
                       <button
                         onClick={() => updateSearchQuery("")}
                         className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                         title="Clear search"
                       >
                         <X className="h-4 w-4" />
                       </button>
                     )}
                   </div>
                   <Select
                     value={typeFilter}
                     onValueChange={setTypeFilter}
                   >
                     <SelectTrigger className="w-40">
                       <SelectValue placeholder="Filter by type" />
                     </SelectTrigger>
                     <SelectContent>
                       <SelectItem value="ALL">All Types</SelectItem>
                       <SelectItem value="SOAP_NOTE_TEMPLATE">SOAP Note Template</SelectItem>
                       <SelectItem value="CONVERSATION_PROMPT">Conversation Prompt</SelectItem>
                       <SelectItem value="SECONDARY_PROMPT">Secondary Prompt</SelectItem>
                       <SelectItem value="SYSTEM_MESSAGE">System Message</SelectItem>
                       <SelectItem value="MERGE_CALL_PROMPT">Merge Call Prompt</SelectItem>
                     </SelectContent>
                   </Select>
                 </div>
                 {(searchQuery || typeFilter !== "ALL") && (
                   <div className="mt-3 text-sm text-gray-600">
                     Found {uniqueFilteredTemplateCount} template
                     {uniqueFilteredTemplateCount !== 1 ? "s" : ""}
                     {typeFilter !== "ALL" && ` of type "${typeFilter}"`}
                   </div>
                   )}
               </CardContent>
            </Card>
          }

          {/* Top action bar */}
          <div className="flex justify-end items-center gap-2">
            <Button
              onClick={() => setCompareSetupOpen(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 font-medium"
            >
              <GitCompare className="h-4 w-4" />
              Compare
            </Button>
            <Button
              onClick={() => openNewTemplateDialog(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400 font-medium"
            >
              <Plus className="h-4 w-4" />
              Create New Template
            </Button>
          </div>

          {/* Loading State */}
          {loading && (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-ktx-purple mb-4"></div>
                  <p className="text-gray-600">Loading templates...</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error State */}
          {error && (
            <Card>
              <CardContent className="text-center py-8">
                <p className="text-red-500 text-lg mb-4">{error}</p>
                <Button onClick={fetchTemplates} variant="outline">
                  Retry
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Templates List - Grouped by Name */}
          {!loading && !error && groupedTemplateEntries.length > 0 && (
            <TemplateGroupsList
              groupedTemplateEntries={groupedTemplateEntries}
              formatTimestamp={formatTimestamp}
              onAddVersion={handleAddVersion}
              onDeleteGroup={openDeleteDialog}
              onOpenTemplateDetails={openTemplateDetailsDialog}
              onSetDefault={setTemplateAsDefault}
              onUpdateScope={updateTemplateScope}
              onDeleteVersion={openDeleteDialog}
              compareGroup={compareGroup}
              compareSelection={compareSelection}
              onToggleCompareMode={handleToggleCompareMode}
              onToggleCompareSelect={handleToggleCompareSelect}
              onViewDiff={handleViewDiff}
            />
          )}

          {/* No Templates Found */}
          {!loading && !error && groupedTemplateEntries.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">
                  {searchQuery
                    ? "No templates found matching your search."
                    : "No templates available."}
                </p>
              </CardContent>
            </Card>
          )}

          {/* New Template/Version Dialog */}
          <Dialog
            open={newTemplateDialogOpen}
            onOpenChange={(open) => {
              if (!open) {
                resetNewTemplateForm(); // Reset form when dialog closes
              }
              setNewTemplateDialogOpen(open);
            }}
          >
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900">
                  {isNewTemplate ? "Create New Template" : "Add New Version"}
                </DialogTitle>
                <DialogDescription className="text-gray-700">
                  {isNewTemplate
                    ? "Create a new template with custom content"
                    : `Add a new version to template "${newTemplateFormData.name}"`}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleNewTemplateSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Template Name *
                    </label>
                    <Input
                      type="text"
                      value={newTemplateFormData.name}
                      onChange={handleNameChange}
                      placeholder="Enter template name"
                      required
                      disabled={!isNewTemplate} // Disabled for new versions
                      className={`mt-1 ${templateNameError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {templateNameError && (
                      <p className="mt-1 text-sm text-red-600">{templateNameError}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Version *
                    </label>
                    <Input
                      type="text"
                      value={newTemplateFormData.version}
                      onChange={handleVersionChange}
                      placeholder=""
                      required
                      className={`mt-1 ${templateVersionError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    />
                    {templateVersionError && (
                      <p className="mt-1 text-sm text-red-600">{templateVersionError}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Type
                    </label>
                    <Select
                      value={newTemplateFormData.type}
                      onValueChange={handleTypeChange}
                      disabled={!isNewTemplate} // Disabled for new versions
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SOAP_NOTE_TEMPLATE">
                          SOAP Note Template
                        </SelectItem>
                        <SelectItem value="CONVERSATION_PROMPT">
                          Conversation Prompt
                        </SelectItem>
                        <SelectItem value="SECONDARY_PROMPT">
                          Secondary Prompt
                        </SelectItem>
                        <SelectItem value="SYSTEM_MESSAGE">
                          System Message
                        </SelectItem>
                        <SelectItem value="MERGE_CALL_PROMPT">
                          Merge Call Prompt
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      Scope
                    </label>
                    <Select
                      value={newTemplateFormData.scope}
                      onValueChange={handleScopeChange}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select scope" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="SPECIALTY">SPECIALTY</SelectItem>
                        <SelectItem value="PROVIDER">PROVIDER</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      Template Content *
                    </label>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1.5">
                        <span className="inline-block h-3 w-3 rounded-sm bg-green-200 border border-green-300" />
                        New text
                      </span>
                    </div>
                  </div>
                  <TrackChangesEditor
                    baseText={newTemplateBaseText}
                    value={newTemplateFormData.text}
                    onChange={(next) =>
                      setNewTemplateFormData((prev) => ({ ...prev, text: next }))
                    }
                    placeholder="Enter your template content here..."
                    className="mt-1"
                  />
                </div>

                <DialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setNewTemplateDialogOpen(false);
                      resetNewTemplateForm();
                    }}
                    className="px-4 py-2 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={(isNewTemplate && !!templateNameError) || !!templateVersionError || isSubmitting}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {isNewTemplate ? "Creating..." : "Adding..."}
                      </>
                    ) : (
                      isNewTemplate ? "Create Template" : "Add Version"
                    )}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          {/* Template Details Dialog */}
          <Dialog
            open={templateDetailsDialogOpen}
            onOpenChange={(open) => {
              if (!open) {
                // Reset to template view so the next open doesn't flash the summary
                setShowSummary(false);
              }
              setTemplateDetailsDialogOpen(open);
            }}
          >
            <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-gray-900 flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  {templateDetails?.name || selectedTemplateForDetails?.name}
                </DialogTitle>
                <DialogDescription className="text-gray-700">
                  Version: {templateDetails?.version || selectedTemplateForDetails?.version}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                {loadingDetails ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-ktx-purple"></div>
                    <p className="ml-2 text-gray-600">
                      Loading template details...
                    </p>
                  </div>
                ) : templateDetails ? (
                  <>
                    {/* Compact Metadata */}
                    <div className="flex items-center gap-3 text-sm">
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500">Type:</span>
                        <Badge
                          variant="outline"
                          className={`text-xs ${
                            templateDetails.type === "SOAP_NOTE_TEMPLATE"
                              ? "border-blue-200 bg-blue-50 text-blue-700"
                              : templateDetails.type === "CONVERSATION_PROMPT"
                              ? "border-green-200 bg-green-50 text-green-700"
                              : templateDetails.type === "SECONDARY_PROMPT"
                              ? "border-purple-200 bg-purple-50 text-purple-700"
                              : "border-gray-200 bg-gray-50 text-gray-700"
                          }`}
                        >
                          {templateDetails.type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500">Scope:</span>
                        <Badge
                          variant="outline"
                          className="text-xs border-gray-200 bg-gray-50 text-gray-700"
                        >
                          {templateDetails.scope}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-500">Status:</span>
                        {templateDetails.is_default === true ? (
                          <Badge variant="default" className="text-xs">
                            Default
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-xs border-gray-200 bg-gray-50 text-gray-700"
                          >
                            Not Default
                          </Badge>
                        )}
                      </div>
                      {/* Toggles the content area below between the raw
                          template text and the AI-generated summary view.
                          Summaries are only supported for SOAP notes and
                          conversation prompts. */}
                      {(templateDetails.type === "SOAP_NOTE_TEMPLATE" ||
                        templateDetails.type === "CONVERSATION_PROMPT") && (
                        <Button
                          onClick={() => setShowSummary((prev) => !prev)}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2 text-xs px-3 py-1.5 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                        >
                          {showSummary ? (
                            "View Template"
                          ) : (
                            <>
                              <Sparkles className="h-4 w-4 text-gray-500 shrink-0" />
                              View Summary
                            </>
                          )}
                        </Button>
                      )}
                    </div>

                    {/* Content area swaps in place: the summary
                        panel when toggled on, otherwise the template content */}
                    {showSummary &&
                    (templateDetails.type === "SOAP_NOTE_TEMPLATE" ||
                      templateDetails.type === "CONVERSATION_PROMPT") ? (
                      <TemplateSummaryPanel
                        name={templateDetails.name}
                        version={templateDetails.version}
                        type={templateDetails.type}
                      />
                    ) : (
                      templateDetails.text && (
                        <div>
                          <label className="text-sm font-medium text-gray-500 mb-2 block">
                            Template Content
                          </label>
                          <div className="p-4 bg-gray-50 rounded-md border max-h-[60vh] overflow-y-auto">
                            <pre className="text-sm text-gray-900 whitespace-pre-wrap font-mono leading-relaxed">
                              {templateDetails.text}
                            </pre>
                          </div>
                        </div>
                      )
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">
                      No template details available
                    </p>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setTemplateDetailsDialogOpen(false)}
                  className="px-4 py-2 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                >
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Confirmation Dialog */}
          <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
            <DialogContent className="bg-white border-gray-200">
              <DialogHeader>
                <DialogTitle className="text-gray-900">
                  Confirm Deletion
                </DialogTitle>
                <DialogDescription className="text-gray-700">
                  {deleteTarget.version
                    ? `Are you sure you want to delete version "${deleteTarget.version}" of template "${deleteTarget.name}"?`
                    : `Are you sure you want to delete ALL versions of template "${deleteTarget.name}"?`}
                  <br />
                  <br />
                  <strong className="text-red-600">
                    This action cannot be undone.
                  </strong>
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setDeleteDialogOpen(false)}
                  className="px-4 py-2 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteConfirm}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-medium"
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Page-level Compare setup (search + pick two versions) */}
          <TemplateCompareSetup
            open={compareSetupOpen}
            onOpenChange={setCompareSetupOpen}
            groupedTemplateEntries={groupedTemplateEntries}
            formatTimestamp={formatTimestamp}
            onCompare={handleSetupCompare}
          />

          {/* Side-by-side Compare Dialog */}
          <TemplateCompareModal
            open={compareModalOpen}
            onOpenChange={setCompareModalOpen}
            templateName={comparePair.a?.name || ""}
            versionA={comparePair.a}
            versionB={comparePair.b}
            formatTimestamp={formatTimestamp}
          />
        </div>
      </div>
      <Toaster />
    </div>
  );
};

export default Templates;
